import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { Prompt, useParams, useHistory, useLocation } from 'react-router-dom'
import { IconAlertCircle } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import {
  fetchScorecardConfigById,
  fetchScorecardConfigChanges,
  fetchScorecardConfigVersions,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import {
  setScorecardConfig,
  setScorecardConfigChanges,
  setScorecardConfigVersions,
} from '@/reducers/qa-copilot/qa-copilot.redux'
import NoData from '@/components/NoData'
import { Banner } from '@/components/banners/Banner'
import { fetchModels } from '@/reducers/prompts/prompts.actions'
import { Button } from '@/components/buttons/Button'

import { QACopilotNavigation } from './components/QACopilotNavigation'
import { QACopilotSidebar } from './components/QACopilotSidebar'
import { QACopilotEditor } from './components/QACopilotEditor'
import { QACopilotAutoPopulate } from './components/QACopilotAutoPopulate'
import { addDefaultSectionHelper } from './QACopilot.helpers'
import { QACopilotCriteriaPointsModal } from './modals/QACopilotCriteriaPointsModal'
import { QACopilotSettingsModal } from './modals/QACopilotSettingsModal'

import './QACopilot.scss'

export const QACopilotPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { loading, error, dirty, scorecardConfig } = useSelector((state) => state.qaCopilot)
  const { loading: loadingPrompt } = useSelector((state) => state.prompts)
  const { organizationid: organizationId, prompt_editor: isPromptEditor } = useSelector(
    (state) => state.currentUser
  )
  const isBaltoAdmin = organizationId === 1
  const canEditPrompts = isBaltoAdmin || isPromptEditor
  const isCoaching = location.pathname.startsWith('/coaching')
  const url = isCoaching ? '/coaching/scorecards' : '/qa-copilot'

  const { id } = useParams()

  useEffect(() => {
    if (canEditPrompts) {
      dispatch(fetchModels())
    }
  }, [])

  useEffect(() => {
    if (id && id !== 'create') {
      dispatch(fetchScorecardConfigById(id, canEditPrompts))
    }

    if (id === 'create') {
      dispatch(
        setScorecardConfig({
          name: `New ${isCoaching ? 'Coaching' : 'QA Copilot'} Scorecard`,
          sections: [addDefaultSectionHelper()],
          organization_id: isBaltoAdmin ? '' : organizationId,
        })
      )
    }

    return () => {
      // Clear the config and its lists on leave, or the next scorecard shows these until its own lists load
      dispatch(setScorecardConfig(null))
      dispatch(setScorecardConfigVersions([]))
      dispatch(setScorecardConfigChanges([]))
    }
  }, [id])

  useEffect(() => {
    // Saved data lands only through SET_SCORECARD_CONFIG, the one action that clears dirty, so drafts never fetch.
    if (scorecardConfig?.sid && !dirty) {
      dispatch(fetchScorecardConfigVersions(scorecardConfig.sid, scorecardConfig.organization_id))
      dispatch(fetchScorecardConfigChanges(scorecardConfig.sid))
    }
  }, [scorecardConfig])

  if (loading.scorecardConfig || loadingPrompt.prompts) {
    return (
      <div className="empty loading-container" data-testid="qa-copilot-loading">
        <Loader label="Scorecard is loading..." />
      </div>
    )
  }

  if (error.scorecardConfig) {
    return (
      <div className="empty-table" data-testid="qa-copilot-error">
        <NoData headerText="No Scorecard Found">
          <p>No QA Copilot scorecard was found with the provided ID.</p>

          <div className="flex vertical-center gap">
            <Button onClick={() => history.push(url)}>Back to Scorecards</Button>
            <Button brand onClick={() => history.push(`${url}/create`)}>
              Create New Scorecard
            </Button>
          </div>
        </NoData>
      </div>
    )
  }

  const isOutdatedScorecard = scorecardConfig?.id !== scorecardConfig?.latest_scorecard_config_id

  return (
    <>
      <Prompt when={dirty} message="You have unsaved changes. Are you sure you want to leave?" />
      <QACopilotNavigation
        isOutdatedScorecard={isOutdatedScorecard}
        url={url}
        scorecardType={isCoaching ? 'coaching' : 'copilot'}
      />
      <div className="qa-copilot-wrapper" data-testid="qa-copilot-page">
        {isOutdatedScorecard && (
          <Banner
            dataTestId="qa-copilot-page-read-only-banner"
            informative
            header="You are viewing an older version of this scorecard."
            icon={<IconAlertCircle />}
          >
            <p>
              This version is read-only & cannot be edited.{' '}
              <a
                href="#"
                onClick={() => {
                  history.push(`${url}/${scorecardConfig.latest_scorecard_config_id}`)
                }}
              >
                Click here
              </a>
              {` to jump to latest version, or restore this version if you'd like to use it.`}
            </p>
          </Banner>
        )}
        <div className="qa-copilot-grid">
          <QACopilotSidebar />
          <div className="qa-copilot-main-content">
            <QACopilotEditor isOutdatedScorecard={isOutdatedScorecard} />
            <QACopilotAutoPopulate isOutdatedScorecard={isOutdatedScorecard} />
          </div>
        </div>
      </div>
      <QACopilotCriteriaPointsModal isOutdatedScorecard={isOutdatedScorecard} />
      <QACopilotSettingsModal
        isCreate={id === 'create'}
        isOutdatedScorecard={isOutdatedScorecard}
      />
    </>
  )
}

export default withLDConsumer()(QACopilotPage)
